import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { isEqual, isFunction } from "lodash";

export interface QueryParams {
  [key: string]: string | number | undefined;
}

interface LawTransactionsContextProps {
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => void;
}

const LawTransactionsContext = createContext<LawTransactionsContextProps | undefined>(undefined);

export function useLawTransactionsContext(): LawTransactionsContextProps {
  const context = useContext(LawTransactionsContext);
  if (!context) {
    throw new Error("useLawTransactionsContext must be used within a LawTransactionsProvider");
  }
  return context;
}

interface LawTransactionsProviderProps {
  children: ReactNode;
}

export function LawTransactionsProvider({ children }: LawTransactionsProviderProps) {
  const history = useHistory();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});

  const setQueryParams = useCallback(
    (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => {
      setQueryParamsBase(prevQueryParams => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }

        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }

        for (let key in nextQueryParams) {
          if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) {
            delete nextQueryParams[key];
          }
        }

        const path = history.location.pathname;
        const newQueryParams = new URLSearchParams(nextQueryParams as Record<string, string>);
        history.replace(`${path}?${newQueryParams.toString()}`);

        return nextQueryParams;
      });
    },
    [history]
  );

  const value: LawTransactionsContextProps = {
    queryParams,
    setQueryParamsBase,
    setQueryParams,
  };

  return <LawTransactionsContext.Provider value={value}>{children}</LawTransactionsContext.Provider>;
}